import React from "react";
import { useParams } from "react-router-dom";
import { apiPost, useGet } from "../hooks/useApi.js";
import { ChevronLeft, ChevronRight, Calendar, Clock, Check } from "lucide-react";

type Details = {
  status: string;
  expiresAt: string;
  appointmentType: { name: string; description: string | null; durationMinutes: number } | null;
  scheduledAt: string | null;
  scheduledEnd: string | null;
  technicianName: string | null;
  customerName: string | null;
};

type Availability = {
  timezone: string;
  durationMinutes: number;
  technicians: Array<{
    id: string;
    name: string;
    slots: Array<{ startsAt: string; endsAt: string }>;
  }>;
};

// Group all slots by date (YYYY-MM-DD) across all technicians
function groupByDate(techs: Availability["technicians"]): Map<string, Array<{ startsAt: string; endsAt: string; technicianId: string; technicianName: string }>> {
  const map = new Map<string, Array<{ startsAt: string; endsAt: string; technicianId: string; technicianName: string }>>();
  for (const t of techs) {
    for (const s of t.slots) {
      const d = new Date(s.startsAt);
      const dateKey = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
      const arr = map.get(dateKey) ?? [];
      arr.push({ ...s, technicianId: t.id, technicianName: t.name });
      map.set(dateKey, arr);
    }
  }
  // Sort each day's slots by start time
  for (const [_, arr] of map) {
    arr.sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
  }
  return map;
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function PublicBookingPage() {
  const { token = "" } = useParams();
  const d = useGet<Details>(token ? "/api/public/book/" + token : null, [token]);
  const a = useGet<Availability>(token ? "/api/public/book/" + token + "/availability" : null, [token, d.data?.status]);
  const [selectedSlot, setSelectedSlot] = React.useState<{ technicianId: string; startsAt: string } | null>(null);
  const [selectedDate, setSelectedDate] = React.useState<string | null>(null);
  const [calendarMonth, setCalendarMonth] = React.useState(() => { const now = new Date(); return new Date(now.getFullYear(), now.getMonth(), 1); });
  const [form, setForm] = React.useState({ customerName: "", customerPhone: "", customerEmail: "" });
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  // Group slots by date
  const slotsByDate = React.useMemo(() => groupByDate(a.data?.technicians ?? []), [a.data]);
  const availableDateKeys = React.useMemo(() => new Set(slotsByDate.keys()), [slotsByDate]);

  async function act(kind: "book" | "reschedule" | "cancel") {
    setBusy(true);
    setError("");
    try {
      await apiPost("/api/public/book/" + token + "/" + kind, kind === "cancel" ? {} : { ...form, customerEmail: form.customerEmail || undefined, ...selectedSlot });
      d.refetch(); a.refetch(); setSelectedSlot(null); setSelectedDate(null);
    } catch (e) { setError(e instanceof Error ? e.message : "Request failed"); }
    finally { setBusy(false); }
  }

  if (d.loading) return <main className="min-h-screen grid place-items-center p-6"><p role="status">Loading your scheduling page…</p></main>;
  if (d.error || !d.data) return <main className="min-h-screen grid place-items-center p-6"><div role="alert" className="max-w-md text-center"><h1 className="text-2xl font-semibold">This link isn't available</h1><p>{d.error ?? "Please ask for a new booking link."}</p></div></main>;

  const cancelled = d.data.status === "cancelled";
  const booked = Boolean(d.data.scheduledAt) && !cancelled;

  // Calendar grid for the current month
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const firstOfMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
  const startDay = firstOfMonth.getDay();
  const daysInMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0).getDate();
  const calendarCells: Array<{ date: Date | null; key: string }> = [];
  for (let i = 0; i < startDay; i++) calendarCells.push({ date: null, key: "empty-" + i });
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day);
    calendarCells.push({ date, key: "day-" + day });
  }

  // Slots for the selected date
  const daySlots = selectedDate ? (slotsByDate.get(selectedDate) ?? []) : [];

  return (
    <main className="min-h-screen bg-slate-50 p-4 sm:p-8 text-slate-900">
      <div className="mx-auto max-w-4xl rounded-2xl bg-white shadow-sm border p-5 sm:p-8 space-y-6">
        <header>
          <p className="text-sm font-medium text-brand-700">TechSys Connect scheduling</p>
          <h1 className="text-3xl font-semibold">{d.data.appointmentType?.name ?? "Appointment"}</h1>
          {d.data.appointmentType?.description && <p className="mt-2 text-slate-600">{d.data.appointmentType.description}</p>}
          <p className="mt-1 text-xs text-slate-500">{a.data?.durationMinutes ?? d.data.appointmentType?.durationMinutes} min · {a.data?.timezone ?? "local time"}</p>
        </header>

        {cancelled && <section className="rounded-xl bg-slate-100 border p-4" role="status"><h2 className="font-semibold">Appointment cancelled</h2><p>This appointment is no longer scheduled.</p></section>}

        {booked && (
          <section className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
            <h2 className="font-semibold flex items-center gap-1"><Check className="h-4 w-4" /> Appointment confirmed</h2>
            <p>{new Date(d.data.scheduledAt!).toLocaleString([], { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })} with {d.data.technicianName}</p>
            <button disabled={busy} onClick={() => act("cancel")} className="mt-3 text-sm underline text-red-700">Cancel appointment</button>
          </section>
        )}

        {!cancelled && (
          <section>
            <h2 className="text-lg font-semibold mb-4">{booked ? "Choose a new time" : "Pick a date, then a time"}</h2>

            {a.loading ? (
              <p role="status">Checking live availability…</p>
            ) : a.error ? (
              <p role="alert">Couldn't load availability. Please try again.</p>
            ) : availableDateKeys.size === 0 ? (
              <p className="rounded border border-dashed p-6 text-center">No times are currently available. Please check back later.</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Calendar */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <button type="button" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))} className="rounded-lg border p-2 hover:bg-slate-50" aria-label="Previous month"><ChevronLeft className="h-4 w-4" /></button>
                    <h3 className="font-semibold">{MONTHS[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}</h3>
                    <button type="button" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))} className="rounded-lg border p-2 hover:bg-slate-50" aria-label="Next month"><ChevronRight className="h-4 w-4" /></button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center text-xs text-slate-500">
                    {DOW.map(d => <div key={d} className="font-medium py-1">{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {calendarCells.map(cell => {
                      if (!cell.date) return <div key={cell.key} />;
                      const dateKey = cell.date.getFullYear() + "-" + String(cell.date.getMonth() + 1).padStart(2, "0") + "-" + String(cell.date.getDate()).padStart(2, "0");
                      const isAvailable = availableDateKeys.has(dateKey);
                      const isPast = cell.date < today;
                      const isSelected = selectedDate === dateKey;
                      return (
                        <button
                          key={cell.key}
                          type="button"
                          disabled={!isAvailable || isPast}
                          onClick={() => { setSelectedDate(dateKey); setSelectedSlot(null); }}
                          className={"aspect-square rounded-lg text-sm font-medium transition-colors " + (isSelected ? "bg-brand-600 text-white" : isAvailable && !isPast ? "bg-brand-50 text-brand-700 hover:bg-brand-100 border border-brand-200" : "text-slate-300 cursor-not-allowed")}
                        >
                          {cell.date.getDate()}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-500 flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-brand-50 border border-brand-200" /> Available</p>
                </div>

                {/* Time slots for selected day */}
                <div className="space-y-3">
                  {!selectedDate ? (
                    <div className="flex flex-col items-center justify-center h-full text-center text-slate-400 py-12">
                      <Calendar className="h-10 w-10 mb-2" />
                      <p className="text-sm">Select a date to see available times</p>
                    </div>
                  ) : daySlots.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center text-slate-400 py-12">
                      <Clock className="h-10 w-10 mb-2" />
                      <p className="text-sm">No times available on this day</p>
                      <p className="text-xs">Try another date</p>
                    </div>
                  ) : (
                    <>
                      <h3 className="font-semibold text-sm">{new Date(selectedDate + "T00:00:00").toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}</h3>
                      <div className="grid grid-cols-2 gap-2">
                        {daySlots.map(s => {
                          const time = new Date(s.startsAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
                          const isSelected = selectedSlot?.startsAt === s.startsAt && selectedSlot?.technicianId === s.technicianId;
                          return (
                            <button
                              key={s.startsAt + s.technicianId}
                              type="button"
                              aria-pressed={isSelected}
                              onClick={() => setSelectedSlot({ technicianId: s.technicianId, startsAt: s.startsAt })}
                              className={"rounded-lg border p-3 text-sm font-medium transition-colors " + (isSelected ? "bg-brand-600 text-white border-brand-600" : "hover:bg-slate-50 border-slate-200")}
                            >
                              {time}
                              {a.data!.technicians.length > 1 && <span className="block text-xs font-normal opacity-70">{s.technicianName}</span>}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </section>
        )}

        {!cancelled && selectedSlot && (
          <form onSubmit={(e) => { e.preventDefault(); void act(booked ? "reschedule" : "book"); }} className="space-y-3 border-t pt-5">
            <h3 className="font-semibold">Your details</h3>
            <p className="text-sm text-slate-600">{new Date(selectedSlot.startsAt).toLocaleString([], { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}{(() => { const t = a.data?.technicians.find(t => t.id === selectedSlot.technicianId); return t ? " with " + t.name : ""; })()}</p>
            <label className="block text-sm">Your name<input required className="mt-1 w-full rounded border p-3" value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} /></label>
            <label className="block text-sm">Mobile phone<input required inputMode="tel" className="mt-1 w-full rounded border p-3" value={form.customerPhone} onChange={(e) => setForm({ ...form, customerPhone: e.target.value })} /></label>
            <label className="block text-sm">Email (optional)<input type="email" className="mt-1 w-full rounded border p-3" value={form.customerEmail} onChange={(e) => setForm({ ...form, customerEmail: e.target.value })} /></label>
            {error && <p role="alert" className="text-red-700">{error}</p>}
            <button disabled={busy} className="w-full rounded bg-brand-600 p-3 font-medium text-white disabled:opacity-50">{busy ? "Saving…" : booked ? "Reschedule appointment" : "Confirm appointment"}</button>
          </form>
        )}
      </div>
    </main>
  );
}